import { PropsWithChildren, useCallback, useEffect } from "react";

import { useTranslation } from "react-i18next";

import {
  CssBaseline,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  Skeleton,
  Alert,
  Link,
} from "@mui/material";

import styled from "@emotion/styled";
import { darken } from "polished";

import GlobalStyle from "components/GlobalStyle";
import Loader from "components/Loader";

import { usePersistEntity } from "hooks/usePersistEntity";
import useUserPreferencesStore from "hooks/useUserPreferences";

import { useTenantById, useTenantSearch } from "fetch/tenant";
import { useUserProfileById } from "fetch/userProfile";

import { TenantPayloadGet } from "domain/entities/tenant";

import { getDecodedToken } from "utils/jwt";

export const TenantSelectPage = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "component.tenantSelector",
  });
  const storage = useUserPreferencesStore();

  const {
    data: user,
    refetch: refetchUser,
    isError,
    isFetching,
    isLoading,
  } = useUserProfileById({
    variables: {
      pathParams: {
        userId: getDecodedToken()?.userId ?? "",
      },
    },
    enabled: !!getDecodedToken()?.userId,
    select: (data) => data.payload,
  });

  const { data: tenant } = useTenantById({
    variables: {
      pathParams: {
        tenantId: storage.tenantId,
      },
    },
    enabled: !!storage.tenantId,
    select: (data) => data.payload,
  });

  usePersistEntity(user, storage.setUser, storage.user?.identifier.id);

  useEffect(() => {
    if (user?.tenants && user?.defaults.tenantId) {
      const tenantReference = user?.tenants.find(
        (tenant) => tenant.id === user?.defaults.tenantId,
      );

      if (tenantReference) {
        storage.setTenantId(tenantReference.id);
      }

      return;
    }

    if (user?.tenants) {
      storage.setTenantId(user?.tenants[0].id);
    }
    // eslint-disable-next-line
  }, [user?.tenants, user?.defaults.tenantId]);

  useEffect(() => {
    if (tenant) {
      storage.setTenant(tenant);
      window.location.replace("/");
    }
    // eslint-disable-next-line
  }, [tenant]);

  if (isFetching || user?.defaults.tenantId)
    return (
      <Box
        style={{
          height: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader />
      </Box>
    );

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <TenantSelect>
        <List
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "1rem",
            flexDirection: "column",
          }}
        >
          {isFetching || isLoading ? (
            <TenantSkeleton>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ borderRadius: "50%" }}
              />
              <Skeleton variant="text" width={200} height={40} />
            </TenantSkeleton>
          ) : (
            <Tenant />
          )}
        </List>
        {isError && !isLoading ? (
          <Alert severity="error">
            {t("error")}

            <Link component="button" onClick={() => refetchUser()}>
              {t("tryAgain")}
            </Link>
          </Alert>
        ) : null}
      </TenantSelect>
    </Root>
  );
};

const TenantSelect = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "component.tenantSelector",
  });

  return (
    <TenantSelectorContainer>
      <Divider />
      <Typography
        variant="h3"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          marginTop: "1rem",
        }}
      >
        {t("headerMsg")}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          marginBottom: "2rem",
        }}
      >
        {t("subHeaderMsg")}
      </Typography>
      {children}
    </TenantSelectorContainer>
  );
};

const Tenant = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "component.tenantSelector",
  });
  const setTenant = useUserPreferencesStore((state) => state.setTenant);
  const activeTenant = useUserPreferencesStore((state) => state.tenant);

  const { data: tenants } = useTenantSearch({}, { queryParams: {} }, {});

  const handleClickTenant = useCallback(
    (tenantReference: TenantPayloadGet) => {
      if (tenants?.successful) {
        const foundTenant = tenants.payload?.find(
          (tenant) => tenant.identifier.id === tenantReference.identifier.id,
        );
        if (foundTenant) setTenant(foundTenant);
        window.location.replace("/");
      }
    },
    [setTenant, tenants?.payload, tenants?.successful],
  );

  return (
    <>
      {tenants?.payload.map((tenant, index) => {
        return (
          <TenantContainer key={tenant.identifier.id}>
            <TenantListItem onClick={() => handleClickTenant(tenant)}>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 5 }}>
                <img
                  alt="avatar"
                  height={40}
                  width={40}
                  src={tenant.image}
                  loading="lazy"
                  style={{ borderRadius: "10%" }}
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6">{tenant.name}</Typography>
                  <Typography variant="body1">{tenant.description}</Typography>
                </Box>
              </Box>
              {tenant.identifier.id === activeTenant?.identifier?.id ? (
                <Typography variant="body1" color="GrayText">
                  {t("active")}
                </Typography>
              ) : null}
            </TenantListItem>
            {index !== tenants.payload.length - 1 ? (
              <Divider component="li" flexItem />
            ) : null}
          </TenantContainer>
        );
      })}
    </>
  );
};

const Root = styled(Box)`
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
  background-color: ${(props) => props.theme.sidebar.header.background};
`;

const TenantContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const TenantSelectorContainer = styled(Box)`
  border: 0.5px solid ${(props) => props.theme.palette.divider};
  min-height: auto;
  min-width: 30rem;
  border-radius: 4px;
  background-color: ${(props) => props.theme.palette.background.default};
`;

const TenantListItem = styled(ListItem)`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 2.5rem;
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      darken(0.03, props.theme.palette.background.default)};
  }
`;

const TenantSkeleton = styled(ListItem)`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  justify-content: flex-start;
  width: 100%;
  padding: 0.5rem 3rem 0.5rem 3rem;
`;
